import { useContext, useMemo } from "react"
import { HStack, Link, Text, VStack } from "@chakra-ui/react"
import { Field } from "@hydrologiq/shared/chakra/Field"
import { NativeSelectField, NativeSelectRoot } from "@hydrologiq/shared/chakra/NativeSelect"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import {
  SearchCustomersDocument,
  type SearchCustomersQuery,
  type SearchCustomersQueryVariables,
  GetCustomerSitesDocument,
  type GetCustomerSitesQuery,
  type GetCustomerSitesQueryVariables,
} from "@hydrologiq/gql-experience/graphql"
import {
  CustomerSiteSelectorStateContext,
  CustomerSiteSelectorActionsContext,
} from "./CustomerSiteSelectorProvider"

export interface CustomerSiteSelectorProps {
  /** When true, hides the customer field (use when customerId is pre-set) */
  hideCustomer?: boolean
  /** When true, hides the site field (use when siteId is pre-set) */
  hideSite?: boolean
  /** Label for the customer field */
  customerLabel?: string
  /** Label for the site field */
  siteLabel?: string
  /** Show a "Create new" link next to the customer dropdown */
  onCreateCustomer?: () => void
  /** Show a "Create new" link next to the site dropdown */
  onCreateSite?: () => void
  /**
   * Controlled mode: pass external customerId directly.
   * When provided, the component does NOT read from CustomerSiteSelectorProvider.
   */
  customerId?: string | null
  /**
   * Controlled mode: pass external siteId directly.
   */
  siteId?: string | null
  /**
   * Controlled mode: called when the user selects a customer.
   * Receives (customerId, customerName).
   */
  onCustomerChange?: (customerId: string | null, customerName?: string | null) => void
  /**
   * Controlled mode: called when the user selects a site.
   * Receives (siteId, siteName).
   */
  onSiteChange?: (siteId: string | null, siteName?: string | null) => void
}

/**
 * Generic customer/site cascading selector component.
 *
 * Supports two usage modes:
 *
 * 1. **Provider mode** (default) — wrap in `CustomerSiteSelectorProvider` and the
 *    component manages state via context.
 *
 * 2. **Controlled mode** — pass `customerId`, `siteId`, `onCustomerChange`, and
 *    `onSiteChange` props directly. No provider needed. Useful when embedding in
 *    an existing form that already manages its own state (e.g. AssignAssetDialog).
 *
 * In either mode, pass `onCreateCustomer` / `onCreateSite` to show "+ Create new"
 * links that trigger external creation flows.
 *
 * Usage (provider mode):
 * ```tsx
 * <CustomerSiteSelectorProvider>
 *   <CustomerSiteSelector
 *     onCreateCustomer={() => setScreen("create-customer")}
 *     onCreateSite={() => setScreen("create-site")}
 *   />
 * </CustomerSiteSelectorProvider>
 * ```
 *
 * Usage (controlled mode):
 * ```tsx
 * <CustomerSiteSelector
 *   customerId={state.customerId}
 *   siteId={state.siteId}
 *   onCustomerChange={(id, name) => dispatch({ type: "SET_CUSTOMER", id, name })}
 *   onSiteChange={(id, name) => dispatch({ type: "SET_SITE", id, name })}
 *   onCreateCustomer={() => navigateToCreateCustomer()}
 * />
 * ```
 */
const CustomerSiteSelector = ({
  hideCustomer = false,
  hideSite = false,
  customerLabel = "Customer",
  siteLabel = "Site",
  onCreateCustomer,
  onCreateSite,
  customerId: controlledCustomerId,
  siteId: controlledSiteId,
  onCustomerChange,
  onSiteChange,
}: CustomerSiteSelectorProps) => {
  // Determine if we're in controlled mode
  const isControlled = onCustomerChange !== undefined || onSiteChange !== undefined

  // Read from provider context (returns undefined if no provider wraps us)
  const providerState = useContext(CustomerSiteSelectorStateContext)
  const providerActions = useContext(CustomerSiteSelectorActionsContext)

  const customerId = isControlled ? (controlledCustomerId ?? null) : (providerState?.customerId ?? null)
  const siteId = isControlled ? (controlledSiteId ?? null) : (providerState?.siteId ?? null)

  const handleCustomerSelect = isControlled
    ? (id: string | null, name?: string | null) => onCustomerChange?.(id, name)
    : (id: string | null, name?: string | null) => providerActions?.setCustomer(id, name)

  const handleSiteSelect = isControlled
    ? (id: string | null, name?: string | null) => onSiteChange?.(id, name)
    : (id: string | null, name?: string | null) => providerActions?.setSite(id, name)

  return (
    <VStack gap={4} width="100%" align="stretch">
      {!hideCustomer && (
        <CustomerField
          label={customerLabel}
          customerId={customerId}
          onSelect={handleCustomerSelect}
          onCreateNew={onCreateCustomer}
        />
      )}
      {!hideSite && (
        <SiteField
          label={siteLabel}
          customerId={customerId}
          siteId={siteId}
          onSelect={handleSiteSelect}
          onCreateNew={onCreateSite}
        />
      )}
    </VStack>
  )
}

export default CustomerSiteSelector

// --- Internal field components ---

interface CustomerFieldProps {
  label: string
  customerId: string | null
  onSelect: (customerId: string | null, customerName?: string | null) => void
  onCreateNew?: () => void
}

const CustomerField = ({ label, customerId, onSelect, onCreateNew }: CustomerFieldProps) => {
  const { data, loading } = useQueryWithEndpoint<SearchCustomersQuery, SearchCustomersQueryVariables>(
    SearchCustomersDocument,
    {
      variables: { query: "", page: 1, per_page: 100 },
      fetchPolicy: "cache-and-network",
    },
    "primary",
  )

  const customers = useMemo(
    () => data?.customers?.data?.map((c) => ({ id: c.id, name: c.name })) ?? [],
    [data],
  )

  const items = useMemo(
    () => [
      { value: "", label: loading ? "Loading..." : "Select customer..." },
      ...customers.map((c) => ({ value: c.id, label: c.name })),
    ],
    [customers, loading],
  )

  return (
    <Field
      label={
        <HStack justifyContent="space-between" width="100%">
          <Text>{label}</Text>
          {onCreateNew && (
            <Link
              variant="secondary"
              fontSize="xs"
              onClick={(e) => {
                e.preventDefault()
                onCreateNew()
              }}
              cursor="pointer"
            >
              + Create new
            </Link>
          )}
        </HStack>
      }
      required
    >
      <NativeSelectRoot disabled={loading}>
        <NativeSelectField
          value={customerId ?? ""}
          onChange={(e) => {
            const id = e.target.value || null
            const name = customers.find((c) => c.id === id)?.name ?? null
            onSelect(id, name)
          }}
          items={items}
        />
      </NativeSelectRoot>
    </Field>
  )
}

interface SiteFieldProps {
  label: string
  customerId: string | null
  siteId: string | null
  onSelect: (siteId: string | null, siteName?: string | null) => void
  onCreateNew?: () => void
}

const SiteField = ({ label, customerId, siteId, onSelect, onCreateNew }: SiteFieldProps) => {
  const { data, loading } = useQueryWithEndpoint<GetCustomerSitesQuery, GetCustomerSitesQueryVariables>(
    GetCustomerSitesDocument,
    {
      variables: { id: customerId || "" },
      skip: !customerId,
      fetchPolicy: "cache-and-network",
    },
    "primary",
  )

  const sites = useMemo(
    () => data?.customerSites?.data?.map((s) => ({ id: s.id, name: s.name })) ?? [],
    [data],
  )

  const items = useMemo(
    () => [
      {
        value: "",
        label: !customerId ? "Select customer first..." : loading ? "Loading..." : "Select site...",
      },
      ...sites.map((s) => ({ value: s.id, label: s.name })),
    ],
    [sites, loading, customerId],
  )

  return (
    <Field
      label={
        <HStack justifyContent="space-between" width="100%">
          <Text>{label}</Text>
          {onCreateNew && customerId && (
            <Link
              variant="secondary"
              fontSize="xs"
              onClick={(e) => {
                e.preventDefault()
                onCreateNew()
              }}
              cursor="pointer"
            >
              + Create new
            </Link>
          )}
        </HStack>
      }
      required
    >
      <NativeSelectRoot disabled={!customerId || loading}>
        <NativeSelectField
          value={siteId ?? ""}
          onChange={(e) => {
            const id = e.target.value || null
            const name = sites.find((s) => s.id === id)?.name ?? null
            onSelect(id, name)
          }}
          items={items}
        />
      </NativeSelectRoot>
    </Field>
  )
}
